// src/components/TrackerBoard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, doc, onSnapshot, setDoc, deleteDoc, query, where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../AuthContext';
import {
  PHASES, PHASE_COLORS, TRACK_STEPS, LP_STEPS,
  PLAT_DELIVERABLES, LP_PLATFORMS, LP_DELIVERABLES, resolveLabel,
  platShort, platColor, newClient,
} from '../constants';
import ClientCard from './ClientCard';
import ClientModal from './ClientModal';
import ImportModal from './ImportModal';

function alertLevel(c) {
  if (c.onHold || c.phase === 'postlaunch' || c.phase === 'cancelled') return '';
  const d = c.lastChecked ? Math.floor((new Date() - new Date(c.lastChecked)) / 86400000) : 999;
  if (d >= 7) return 'urgent';
  if (d >= 4) return 'stale';
  return '';
}

function sortCards(arr) {
  return [...arr].sort((a, b) => {
    const ai = a.important ? 0 : 1, bi = b.important ? 0 : 1;
    if (ai !== bi) return ai - bi;
    const av = alertLevel(a) === 'urgent' ? 0 : alertLevel(a) === 'stale' ? 1 : 2;
    const bv = alertLevel(b) === 'urgent' ? 0 : alertLevel(b) === 'stale' ? 1 : 2;
    return av - bv;
  });
}

function missingDelivCount(c) {
  let miss = 0;
  (c.platforms || []).forEach(p => {
    const aType = p.accountType || 'new';
    const dKey = (p.name === 'LSA' && aType === 'new') ? 'LSA_new' : p.name;
    const masterList = (PLAT_DELIVERABLES[dKey] || []).map(d => resolveLabel(d, aType));
    masterList.forEach(d => { if (!p.deliverables?.[d]) miss++; });
    if (LP_PLATFORMS.includes(p.name) && p.lpDelivEnabled !== false) {
      LP_DELIVERABLES.forEach(d => { if (!p.lpDeliverables?.[d]) miss++; });
    }
  });
  return miss;
}

const PHASE_BADGE = ({ phase }) => {
  const ph = PHASES.find(p => p.id === phase);
  const c = PHASE_COLORS[phase] || {};
  return (
    <span style={{
      fontSize: 10, padding: '2px 7px', borderRadius: 3, fontWeight: 700,
      background: c.bg, color: c.text, whiteSpace: 'nowrap',
    }}>
      {ph ? ph.label : phase}
    </span>
  );
};

export default function TrackerBoard({ ownerUid, ownerName, isOwn }) {
  const { user, userProfile } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('board');
  const [search, setSearch] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [modalClient, setModalClient] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [toast, setToast] = useState('');

  // Real-time listener for this owner's clients
  useEffect(() => {
    const q = query(collection(db, 'clients'), where('ownerUid', '==', ownerUid));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      setClients(data.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)));
      setLoading(false);
    });
    return unsub;
  }, [ownerUid]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 2400);
  }

  function openNew() {
    setModalClient(newClient(ownerUid, ownerName));
  }

  function openEdit(id) {
    if (isViewer) {
      // Viewers can open cards in read-only view mode
      const c = clients.find(x => x.id === id);
      if (c) setModalClient({ ...c });
    } else {
      const c = clients.find(x => x.id === id);
      if (c) setModalClient({ ...c });
    }
  }

  async function handleSave(data) {
    // Sanitize: remove undefined values which Firestore rejects
    function sanitize(obj) {
      if (Array.isArray(obj)) return obj.map(sanitize);
      if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
          Object.entries(obj)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, sanitize(v)])
        );
      }
      return obj;
    }
    try {
      const ref = doc(db, 'clients', data.id);
      await setDoc(ref, sanitize(data), { merge: true });
      showToast(data.name ? `Saved: ${data.name} ✓` : 'Saved ✓');
    } catch (e) {
      console.error('Save error:', e);
      showToast('⚠ Save failed: ' + e.message);
    }
  }

  async function handleDelete() {
    if (!modalClient) return;
    await deleteDoc(doc(db, 'clients', modalClient.id));
    setModalClient(null);
    showToast('Deleted');
  }

  async function handleDuplicate() {
    if (!modalClient) return;
    const newId = Date.now().toString(36) + Math.random().toString(36).slice(2);
    const duplicate = {
      ...modalClient,
      id: newId,
      name: modalClient.name + ' (Copy)',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      actualLaunch: '',
      kickoff: '',
      projLaunch: '',
      lastChecked: '',
    };
    function sanitize(obj) {
      if (Array.isArray(obj)) return obj.map(sanitize);
      if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(Object.entries(obj).filter(([,v]) => v !== undefined).map(([k,v]) => [k, sanitize(v)]));
      }
      return obj;
    }
    await setDoc(doc(db, 'clients', newId), sanitize(duplicate));
    setModalClient(duplicate);
    showToast(`Duplicated: ${duplicate.name} ✓`);
  }

  async function toggleImportant(id) {
    if (isViewer) return;
    const c = clients.find(x => x.id === id);
    if (!c) return;
    const ref = doc(db, 'clients', id);
    await setDoc(ref, { important: !c.important, updatedAt: Date.now() }, { merge: true });
  }

  // Filtering
  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const mq = !q || c.name?.toLowerCase().includes(q) || (c.cids || '').toLowerCase().includes(q) || (c.notes || '').toLowerCase().includes(q) || (c.csm || '').toLowerCase().includes(q);
    const mph = !filterPhase || c.phase === filterPhase;
    const mpl = !filterPlatform || (c.platforms || []).some(p => p.name === filterPlatform);
    let ms = true;
    if (filterStatus === 'active') ms = !c.onHold;
    else if (filterStatus === 'hold') ms = !!c.onHold;
    else if (filterStatus === 'important') ms = !!c.important;
    else if (filterStatus === 'stale') ms = alertLevel(c) === 'stale';
    else if (filterStatus === 'urgent') ms = alertLevel(c) === 'urgent';
    return mq && mph && mpl && ms;
  });

  // Stats
  const total = clients.filter(c => c.phase !== 'postlaunch' && c.phase !== 'cancelled').length;
  const active = clients.filter(c => !c.onHold && c.phase !== 'postlaunch' && c.phase !== 'cancelled').length;
  const onHold = clients.filter(c => c.onHold).length;
  const postlaunch = clients.filter(c => c.phase === 'postlaunch').length;
  const cancelled = clients.filter(c => c.phase === 'cancelled').length;
  const imp = clients.filter(c => c.important).length;
  const urg = clients.filter(c => alertLevel(c) === 'urgent').length;
  const stale = clients.filter(c => alertLevel(c) === 'stale').length;
  const totalBudget = clients
    .filter(c => !c.onHold && c.phase !== 'postlaunch')
    .reduce((s, c) => s + (c.platforms || []).reduce((ps, p) => ps + (parseFloat(p.budget) || 0), 0), 0);

  const isViewer = userProfile?.role === 'viewer';
  const canEdit = !isViewer && (isOwn || userProfile?.role === 'admin');

  const toolbar = (
    <div style={{
      background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.1)',
      padding: '7px 14px', display: 'flex', alignItems: 'center',
      gap: 6, flexWrap: 'wrap', flexShrink: 0,
    }}>
      {canEdit && (
        <button onClick={openNew} style={{
          fontSize: 12, padding: '5px 12px', background: '#185FA5', color: '#fff',
          border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700,
          fontFamily: 'inherit',
        }}>
          + New Client
        </button>
      )}
      {isOwn && !isViewer && (
        <button onClick={() => setShowImport(true)} style={{
          fontSize: 12, padding: '5px 12px', background: '#f5f5f3', color: '#555',
          border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7, cursor: 'pointer', fontWeight: 600,
          fontFamily: 'inherit',
        }}>
          📂 Import JSON
        </button>
      )}
      <input
        type="text"
        placeholder="Search clients…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          fontSize: 12, padding: '5px 10px', border: '1px solid rgba(0,0,0,0.18)',
          borderRadius: 7, background: '#f5f5f3', color: '#1a1a18', width: 180,
          fontFamily: 'inherit',
        }}
      />
      <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={selStyle}>
        <option value="">All phases</option>
        {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} style={selStyle}>
        <option value="">All platforms</option>
        {['Google Ads','Microsoft','LSA','Meta','Yelp','Nextdoor'].map(p => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selStyle}>
        <option value="">All status</option>
        <option value="active">Active only</option>
        <option value="hold">On Hold only</option>
        <option value="important">★ Focus Next</option>
        <option value="stale">Needs Check (4d+)</option>
        <option value="urgent">Urgent (7d+)</option>
      </select>
      <div style={{ marginLeft: 'auto', display: 'flex' }}>
        {['board', 'list', 'active'].map(v => (
          <button key={v} onClick={() => setViewMode(v)} style={{
            fontSize: 12, padding: '5px 10px',
            background: viewMode === v ? '#E6F1FB' : 'transparent',
            color: viewMode === v ? '#185FA5' : '#888',
            border: '1px solid rgba(0,0,0,0.18)',
            borderLeft: v !== 'board' ? 'none' : '1px solid rgba(0,0,0,0.18)',
            borderRadius: v === 'board' ? '7px 0 0 7px' : v === 'active' ? '0 7px 7px 0' : 0,
            cursor: 'pointer', fontFamily: 'inherit', fontWeight: viewMode === v ? 700 : 400,
          }}>
            {v === 'board' ? '⊟ Board' : v === 'list' ? '☰ List' : '▶ Active'}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
      Loading clients…
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Stats bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)',
        padding: '6px 14px', display: 'flex', gap: 5, flexWrap: 'wrap',
        alignItems: 'center', flexShrink: 0,
      }}>
        {[
          { label: 'Active', value: active, bg: '#E6F1FB', color: '#185FA5',
            click: () => { setFilterStatus('active'); setFilterPhase(''); } },
          { label: 'On Hold', value: onHold, bg: '#FCEBEB', color: '#A32D2D',
            click: () => setFilterStatus('hold') },
          { label: '🚀 Launched', value: postlaunch, bg: '#dcf0e8', color: '#0F6E56',
            click: () => { setFilterPhase('postlaunch'); setFilterStatus(''); } },
          { label: '✕ Cancelled', value: cancelled, bg: '#f0f0ee', color: '#666664',
            click: () => { setFilterPhase('cancelled'); setFilterStatus(''); } },
          { label: '★ Focus', value: imp, bg: '#E6F1FB', color: '#0C447C',
            click: () => setFilterStatus('important') },
          { label: '⚠ Urgent', value: urg, bg: '#FCEBEB', color: '#A32D2D',
            click: () => setFilterStatus('urgent') },
          { label: '▲ Stale', value: stale, bg: '#FAEEDA', color: '#854F0B',
            click: () => setFilterStatus('stale') },
        ].map(s => (
          <div
            key={s.label}
            onClick={s.click}
            title={`Filter by: ${s.label}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: s.bg, borderRadius: 6, padding: '3px 9px',
              cursor: 'pointer', border: '1px solid transparent',
              transition: 'border 0.1s',
            }}
            onMouseEnter={e => e.currentTarget.style.border = `1px solid ${s.color}55`}
            onMouseLeave={e => e.currentTarget.style.border = '1px solid transparent'}
          >
            <span style={{ fontSize: 16, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>
          </div>
        ))}
        {totalBudget > 0 && (
          <div style={{
            marginLeft: 'auto', fontSize: 11, fontWeight: 700,
            color: '#185FA5', background: '#E6F1FB',
            borderRadius: 6, padding: '3px 10px',
          }}>
            ${totalBudget.toLocaleString()}/mo active
          </div>
        )}
        {(filterStatus || filterPhase) && (
          <button
            onClick={() => { setFilterStatus(''); setFilterPhase(''); }}
            style={{
              fontSize: 10, padding: '3px 8px', background: 'none',
              border: '1px solid rgba(0,0,0,0.18)', borderRadius: 5,
              cursor: 'pointer', color: '#888', fontFamily: 'inherit',
            }}
          >
            ✕ Clear filter
          </button>
        )}
      </div>
      {toolbar}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'board' && <BoardView clients={filtered} onOpen={openEdit} onToggleImportant={toggleImportant} filterPhase={filterPhase} filterStatus={filterStatus} isViewer={isViewer} />}
        {viewMode === 'list' && <ListView clients={filtered} onOpen={openEdit} onToggleImportant={toggleImportant} isViewer={isViewer} />}
        {viewMode === 'active' && <ActiveView clients={clients} onOpen={openEdit} onToggleImportant={toggleImportant} isViewer={isViewer} />}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          background: '#185FA5', color: '#fff', padding: '9px 16px',
          borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 300,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Modal */}
      {modalClient && (
        <ClientModal
          client={modalClient}
          onSave={handleSave}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onClose={() => setModalClient(null)}
        />
      )}
      {/* Import Modal */}
      {showImport && (
        <ImportModal
          ownerUid={ownerUid}
          ownerName={ownerName}
          onClose={() => setShowImport(false)}
          onDone={() => setShowImport(false)}
        />
      )}
    </div>
  );
}

const selStyle = {
  fontSize: 12, padding: '5px 8px', border: '1px solid rgba(0,0,0,0.18)',
  borderRadius: 7, background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
};

function BoardView({ clients, onOpen, onToggleImportant, filterPhase, filterStatus }) {
  // Show Launched column only when explicitly filtered to it
  const showLaunched = filterPhase === 'postlaunch' || filterStatus === 'postlaunch';
  const showCancelled = filterPhase === 'cancelled';
  const visiblePhases = PHASES.filter(p => {
    if (p.id === 'postlaunch') return showLaunched;
    if (p.id === 'cancelled') return showCancelled;
    return true;
  });

  return (
    <div style={{
      flex: 1, overflow: 'hidden',
      overflowX: 'auto', display: 'flex', gap: 8, padding: 10,
    }}>
      {!showLaunched && (
        <div
          onClick={() => {}}
          style={{
            position: 'absolute', bottom: 24, right: 24,
            fontSize: 11, color: '#888', background: '#fff',
            border: '1px solid rgba(0,0,0,0.12)', borderRadius: 20,
            padding: '4px 12px', pointerEvents: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          🚀 Launched column hidden — filter by "Post-Launch" to show
        </div>
      )}
      {visiblePhases.map(phase => {
        const cards = sortCards(clients.filter(c => c.phase === phase.id));
        return (
          <div key={phase.id} style={{
            flexShrink: 0, width: 224, display: 'flex', flexDirection: 'column',
            background: '#f5f5f3', borderRadius: 12,
            border: '1px solid rgba(0,0,0,0.1)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '7px 10px', borderBottom: '1px solid rgba(0,0,0,0.1)',
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase.color, flexShrink: 0 }} />
              <div style={{ fontSize: 11, fontWeight: 700, flex: 1 }}>{phase.label}</div>
              <div style={{
                fontSize: 11, color: '#888', background: '#fff',
                border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, padding: '1px 6px',
              }}>
                {cards.length}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {cards.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '18px 8px', color: '#ccc', fontSize: 11 }}>No clients</div>
              ) : cards.map(c => (
                <ClientCard
                  key={c.id}
                  client={c}
                  onClick={() => onOpen(c.id)}
                  onToggleImportant={onToggleImportant}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListView({ clients, onOpen, onToggleImportant }) {
  const sorted = sortCards(clients);
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: 12,
        background: '#fff', borderRadius: 12, overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.1)',
      }}>
        <thead>
          <tr>
            {['','Client','CID/ID','Phase','Platforms','Budget/mo','CSM/PM','Last Checked','Proj. Launch'].map(h => (
              <th key={h} style={{
                textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700,
                color: '#888', whiteSpace: 'nowrap', background: '#f5f5f3',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(c => {
            const al = alertLevel(c);
            const total = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
            return (
              <tr key={c.id} onClick={() => onOpen(c.id)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => [...e.currentTarget.cells].forEach(td => td.style.background = '#f8f8f8')}
                onMouseLeave={e => [...e.currentTarget.cells].forEach(td => td.style.background = '')}
              >
                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <button
                    onClick={e => { e.stopPropagation(); onToggleImportant(c.id); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: c.important ? '#185FA5' : '#ddd' }}
                  >★</button>
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', borderLeft: al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : c.important ? '3px solid #185FA5' : undefined }}>
                  <strong>{c.name}</strong>
                  {c.onHold && <span style={{ marginLeft: 4, fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>Hold</span>}
                </td>
                <td style={{ padding: '7px 12px', fontFamily: 'monospace', fontSize: 11, color: '#888', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{(c.cids || '').slice(0, 22)}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}><PHASE_BADGE phase={c.phase} /></td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {(c.platforms || []).slice(0, 4).map((p, i) => {
                      const col = platColor(p.name);
                      return <span key={i} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: col.bg, color: col.text }}>{platShort(p.name)}</span>;
                    })}
                  </div>
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{total > 0 ? `$${total.toLocaleString()}` : '—'}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{c.csm || '—'}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', color: al === 'urgent' ? '#A32D2D' : al === 'stale' ? '#854F0B' : 'inherit' }}>{c.lastChecked || '—'}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{c.projLaunch || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ActiveView({ clients, onOpen, onToggleImportant }) {
  const active = sortCards(clients.filter(c => !c.onHold));
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
      <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>
        Showing all <strong>{active.length}</strong> active clients — excludes On Hold
      </div>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: 12,
        background: '#fff', borderRadius: 12, overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.1)',
      }}>
        <thead>
          <tr>
            {['','Client','Phase','Platforms','Access','Budget','Deliverables','CSM/PM','Proj. Launch','Last Checked'].map(h => (
              <th key={h} style={{
                textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700,
                color: '#888', whiteSpace: 'nowrap', background: '#f5f5f3',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {active.map(c => {
            const al = alertLevel(c);
            const total = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
            const missing = missingDelivCount(c);
            const noAccess = (c.platforms || []).filter(p => p.accountType === 'existing' && !p.accessGranted);
            const days = c.lastChecked ? Math.floor((new Date() - new Date(c.lastChecked)) / 86400000) : null;
            return (
              <tr key={c.id} onClick={() => onOpen(c.id)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => [...e.currentTarget.cells].forEach(td => td.style.background = '#f8f8f8')}
                onMouseLeave={e => [...e.currentTarget.cells].forEach(td => td.style.background = '')}
              >
                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                  <button onClick={e => { e.stopPropagation(); onToggleImportant(c.id); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: c.important ? '#185FA5' : '#ddd' }}>★</button>
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', borderLeft: al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : c.important ? '3px solid #185FA5' : undefined }}>
                  <strong>{c.name}</strong>
                  {c.important && <span style={{ marginLeft: 4, fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#E6F1FB', color: '#0C447C' }}>★ Focus</span>}
                  {c.pinNote && <div style={{ fontSize: 10, color: '#854F0B', marginTop: 2 }}>📌 {c.pinNote}</div>}
                  {al === 'urgent' && <div style={{ fontSize: 10, color: '#A32D2D', fontWeight: 700 }}>⚠ {days}d since check — URGENT</div>}
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}><PHASE_BADGE phase={c.phase} /></td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {(c.platforms || []).slice(0, 4).map((p, i) => {
                      const col = platColor(p.name);
                      return <span key={i} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: col.bg, color: col.text }}>{platShort(p.name)}</span>;
                    })}
                  </div>
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  {noAccess.length > 0
                    ? noAccess.map((p, i) => <span key={i} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D', marginRight: 2 }}>{platShort(p.name)}</span>)
                    : <span style={{ fontSize: 11, color: '#27500A' }}>✓</span>}
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{total > 0 ? `$${total.toLocaleString()}` : '—'}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  {missing > 0
                    ? <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 3, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>{missing} needed</span>
                    : <span style={{ fontSize: 11, color: '#27500A' }}>✓ Done</span>}
                </td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{c.csm || '—'}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>{c.projLaunch || '—'}</td>
                <td style={{ padding: '7px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', color: al === 'urgent' ? '#A32D2D' : al === 'stale' ? '#854F0B' : 'inherit' }}>
                  {c.lastChecked || '—'}
                  {al && <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700 }}>▲{days}d</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
